import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { ChevronRight, Column16 } from '@makyoapp/makyo-icon/icon'
import { DD, JustifyVariant, SearchBox, Switch, SwitchSize, useTheme } from '@makyoapp/makyo-ui'

import { TABLE_ROW_ID, TABLE_ROW_ID_NAME } from '@/shared/ag-grid-constants'
import { ColumnTypeIcon } from '@/components/ColumnTypeIcon'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

/**
 * switch box to hide/show a column in layout title
 */
function ShowHideCheck({
  column,
  onToggle,
}: {
  column: ColumnAdapter
  onToggle(colId: string, visible: boolean): void
}) {
  return (
    <div className='flex-c mk-dd-row cursor-pointer'>
      <div className='flex-c w-4/5'>
        <ColumnTypeIcon type={column.type} />
        <div className='max-w-[160px] txt'>{column.name}</div>
      </div>
      <div>
        <Switch
          justify={JustifyVariant.Start}
          size={SwitchSize.XS}
          checked={!column.isHidden}
          onClick={() => {
            const visible = !column.isHidden
            onToggle(column.id, visible)
          }}
        />
      </div>
    </div>
  )
}

/**
 * @todo:
 *  - implement toggle column
 */
export function ColumnVisibility() {
  const { columns, setShowTableRowIDs, showTableRowIDs, table, handleHideColumn, handleHideAllColumns } =
    useGridV2Context()
  const { t } = useTranslation()
  const { state: themeState } = useTheme()
  const [colSearch, setColSearch] = useState('')

  const isAllSelected = columns.every((c) => !c.isHidden)

  const cols = useMemo(() => {
    return colSearch
      ? columns.filter((c) => {
          return c.name.toLowerCase().includes(colSearch.toLowerCase())
        })
      : columns
  }, [columns, colSearch])

  const tableId = table?.id || ''

  const handleToggleAll = (isChecked: boolean) => {
    handleHideAllColumns(!isChecked)
  }

  const isShowRowID = showTableRowIDs?.includes(tableId) ?? false

  const handleToggleRowID = () => {
    if (isShowRowID) {
      setShowTableRowIDs((prev) => prev?.filter((id) => id !== tableId))
    } else {
      setShowTableRowIDs((prev) => {
        return Array.from(new Set([...(prev || []), tableId]))
      })
    }
  }

  return (
    <DD
      className='w-full'
      toggleClassName='mk-ddi mk-dd-row flex-c w-full'
      menuClassName='mk-dd-menu border bdr py-2 w-[220px] max-h-[70vh] overflow-y-auto'
      menuDefaultSize={false}
      placement={themeState.dir === 'rtl' ? 'left' : 'right'}
      toggle={() => {
        return (
          <>
            <div className='flex-c w-full'>
              <Column16 width={18} height={18} />
              <div>{t('Column Visibility')}</div>
            </div>
            <ChevronRight />
          </>
        )
      }}
    >
      <SearchBox
        onSearch={(search) => {
          setColSearch(search)
        }}
        onClearSearch={() => setColSearch('')}
        noBorder
      />

      <label className='flex-c mk-dd-row cursor-pointer'>
        <div className='flex-c w-4/5'>
          <Trans>Select/Unselect All</Trans>
        </div>
        <div>
          <Switch
            checked={isAllSelected}
            justify={JustifyVariant.Start}
            size={SwitchSize.XS}
            onClick={handleToggleAll}
          />
        </div>
      </label>

      <ShowHideCheck
        key={TABLE_ROW_ID}
        column={
          {
            type: ColumnTypes.RowID,
            name: TABLE_ROW_ID_NAME,
            id: TABLE_ROW_ID,
            isHidden: !isShowRowID,
          } as any
        }
        onToggle={handleToggleRowID}
      />

      {cols.map((tableColumn) => {
        return <ShowHideCheck key={tableColumn.id} column={tableColumn} onToggle={handleHideColumn} />
      })}
    </DD>
  )
}
